import React from 'react';
import { Card, CardMedia, Box, Typography, IconButton, ButtonBase } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  ShowStatus,
  STATUS_LABELS,
  TVShow,
  currentSeason,
  newEpisodesBadge,
  progress,
  seasonWatched,
} from '../types';
import { brand, gradients } from '../../../theme/colors';
import { cardFoot, footPink, footSecondary } from '../../../utils/surfaces';

interface TVShowCardProps {
  show: TVShow;
  status: ShowStatus;
  isReadOnly: boolean;
  onOpen: (show: TVShow) => void;
  onMenuClick: (e: React.MouseEvent<HTMLButtonElement>, show: TVShow) => void;
  onTrailer: (show: TVShow) => void;
}

const overlayButton = {
  position: 'absolute',
  top: 4,
  bgcolor: 'rgba(0,0,0,0.55)',
  color: 'common.white',
  backdropFilter: 'blur(4px)',
  '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
} as const;

const formatAirDate = (date: string) =>
  new Date(`${date}T00:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
  });

// The short note on the right of the year: whichever is most useful now.
const detailText = (show: TVShow, status: ShowStatus) => {
  if (show.rewatching) {
    return show.rewatches > 1 ? `Rewatch #${show.rewatches}` : 'Rewatch';
  }
  if (status === 'inProgress' || status === 'givenUp') {
    const season = currentSeason(show);
    if (season) {
      return `S${season.n} · ${seasonWatched(show, season)}/${season.aired}`;
    }
  }
  if (show.tmdb.nextAirDate) return `Next: ${formatAirDate(show.tmdb.nextAirDate)}`;
  if (show.rewatches > 0) return `Watched ${show.rewatches + 1}×`;
  if (status === 'caughtUp') return 'Returning';
  return show.tmdb.status;
};

const TVShowCard = ({
  show,
  status,
  isReadOnly,
  onOpen,
  onMenuClick,
  onTrailer,
}: TVShowCardProps) => {
  const { seen, aired } = progress(show);
  const badge = newEpisodesBadge(show);
  const percent = aired > 0 ? Math.round((seen / aired) * 100) : 0;

  return (
    <Card
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        border: '1px solid',
        borderColor: 'primary.main',
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          src={show.image}
          alt={show.title}
          onClick={() => onOpen(show)}
          sx={{
            width: '100%',
            aspectRatio: '2 / 3',
            objectFit: 'cover',
            display: 'block',
            cursor: 'pointer',
            bgcolor: 'action.hover',
            filter: status === 'givenUp' ? 'grayscale(0.85)' : undefined,
          }}
        />

        {status === 'wantToWatch' && (
          <ButtonBase
            onClick={(e) => {
              e.stopPropagation();
              onTrailer(show);
            }}
            aria-label={`Watch the ${show.title} trailer`}
            sx={{
              ...overlayButton,
              top: 'auto',
              bottom: 8,
              left: 8,
              pl: 0.75,
              pr: 1.25,
              py: 0.5,
              gap: 0.25,
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            <PlayArrowIcon sx={{ fontSize: 18 }} />
            Trailer
          </ButtonBase>
        )}
      </Box>

      {!isReadOnly && (
        <IconButton
          size="small"
          aria-label={`${show.title} options`}
          onClick={(e) => onMenuClick(e, show)}
          sx={{ ...overlayButton, right: 4 }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}

      {badge && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            bgcolor: brand.pink,
            color: brand.white,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 0.3,
            textTransform: 'uppercase',
            pointerEvents: 'none',
          }}
        >
          {badge}
        </Box>
      )}

      <Box
        sx={(theme) => ({
          px: 1.25,
          py: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          ...cardFoot(theme),
        })}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {show.title}
        </Typography>

        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
            fontSize: 12,
            color: footSecondary(theme),
          })}
        >
          <span>{show.year || ''}</span>
          <Box
            component="span"
            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {detailText(show, status)}
          </Box>
        </Box>

        <Box
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'action.hover',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              width: `${percent}%`,
              height: '100%',
              backgroundImage: gradients.primary,
              backgroundSize: percent > 0 ? `${(100 / percent) * 100}% 100%` : undefined,
            }}
          />
        </Box>

        <Box
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
            fontSize: 12,
            fontWeight: 700,
            color: footPink(theme),
          })}
        >
          <span>{STATUS_LABELS[status]}</span>
          <Box component="span" sx={(theme) => ({ color: footSecondary(theme), fontWeight: 600 })}>
            {aired > 0 ? `${seen}/${aired}` : 'Not aired yet'}
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default TVShowCard;
